// ============================================================
// SecaoNucleos.js — Administração de núcleos
//
// Só aparece para admin/coordenador geral (controlado no App.js). Lista
// os núcleos, permite criar um novo e editar modalidades, status ativo e
// os LOCAIS OFICIAIS de treino.
//
// Vários locais por núcleo porque alguns treinam em pistas ou ginásios
// diferentes de forma permanente — um endereço só faria todo treino nos
// demais aparecer como irregular. O raio é o mesmo para todos: uma
// instalação grande se cobre com mais pontos, não com um raio maior.
//
// Toda validação de permissão e de coordenada acontece no servidor
// (Cloud Function gerenciarNucleo) — aqui é só interface.
// ============================================================

import { useState, useEffect } from "react";
import { carregarNucleos, salvarNucleo, excluirNucleo, contarVinculosNucleo } from "../firebase";
import { useNucleos } from "../NucleosContexto";
import { Card, Btn, Input, StatusBox, SeletorLocal } from "../componentes";
import { AZUL, TEXTO, CINZA_ESC, VERDE_LOGO, PERIGO, ALERTA } from "../tema";

// Raio fixo, definido no servidor (functions/index.js). Aparece aqui só
// para exibição — não é campo editável: raio configurável permitiria a
// um treino externo com raio enorme desativar a conferência.
const RAIO_FIXO = 300;

// Mesma chave que o servidor usa: o cadastro tem "BMX" ao lado de "Bmx",
// e por texto puro o pódio de uma grafia não valeria para a outra.
const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);
function chaveDaModalidade(m) {
  return String(m || "").normalize("NFD").replace(DIACRITICOS, "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Quebra de linha escrita sem barra invertida, que some em algumas
// ferramentas de edição e deixa a regex partida no meio.
const QUEBRA_DE_LINHA = String.fromCharCode(10);
const SEPARADOR_DE_LINHA = new RegExp(String.fromCharCode(13) + "?" + String.fromCharCode(10));

// As categorias viajam como lista e são editadas como texto, uma por linha.
// Estas duas funções fazem a tradução, e só.
function textoDasCategorias(mapa) {
  const entrada = mapa && typeof mapa === "object" ? mapa : {};
  const texto = {};
  Object.keys(entrada).forEach((k) => {
    texto[k] = (Array.isArray(entrada[k]) ? entrada[k] : []).join(QUEBRA_DE_LINHA);
  });
  return texto;
}

function listaDasCategorias(texto) {
  const entrada = texto && typeof texto === "object" ? texto : {};
  const mapa = {};
  Object.keys(entrada).forEach((k) => {
    mapa[k] = String(entrada[k] || "")
      .split(SEPARADOR_DE_LINHA)
      .map((c) => c.trim())
      .filter(Boolean);
  });
  return mapa;
}

// Categorias repetidas com grafia diferente, que é o erro que esta lista
// existe para impedir. Devolve os pares, para a tela apontar qual é qual.
function grafiasRepetidas(linhas) {
  const vistas = new Map();
  const repetidas = [];
  linhas.forEach((c) => {
    const k = chaveDaModalidade(c);
    if (!k) return;
    if (vistas.has(k)) repetidas.push([vistas.get(k), c]);
    else vistas.set(k, c);
  });
  return repetidas;
}

// As caixas de categoria, uma por modalidade.
//
// Serve à criação e à edição do núcleo, com o mesmo texto e o mesmo aviso
// de grafia repetida: são o mesmo cadastro feito em dois momentos, e dois
// desenhos para a mesma coisa é o que faz alguém achar que são coisas
// diferentes.
function CaixasDeCategoria({ modalidades, valores, onMudar }) {
  if (!modalidades.length) return null;
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO, marginBottom: 2 }}>
        Categorias de cada modalidade
      </div>
      <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 10, lineHeight: 1.55 }}>
        Uma categoria por linha, na ordem em que as provas correm. Elas viram a lista de
        escolha no cadastro do atleta e no lançamento de resultado. Escreva sempre do
        mesmo jeito, porque <b>Boys 13 anos</b> e <b>Boys - 13</b> contam como duas no
        quadro de medalhas. Modalidade sem lista continua aceitando a categoria digitada
        à mão.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        {modalidades.map((m) => {
          const k = chaveDaModalidade(m);
          const linhas = String(valores[k] || "").split(SEPARADOR_DE_LINHA).map((c) => c.trim()).filter(Boolean);
          const repetidas = grafiasRepetidas(linhas);
          return (
            <div key={m} style={{
              background: "#F8FAFC", border: "1px solid #E5E7EB",
              borderRadius: 9, padding: "10px 12px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{m}</span>
                <span style={{ fontSize: 11.5, color: CINZA_ESC }}>
                  {linhas.length === 0 ? "sem categorias" : `${linhas.length} categoria(s)`}
                </span>
              </div>
              <textarea
                value={valores[k] || ""}
                onChange={(e) => onMudar(k, e.target.value)}
                rows={Math.min(12, Math.max(4, linhas.length + 1))}
                placeholder={["Girls 12 anos", "Boys 13 anos", "Elite Men"].join(QUEBRA_DE_LINHA)}
                style={{
                  width: "100%", boxSizing: "border-box", resize: "vertical",
                  border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 10px",
                  fontSize: 13, fontFamily: "inherit", lineHeight: 1.6, background: "#FFF",
                }} />
              {repetidas.length > 0 && (
                <div style={{ fontSize: 11.5, color: PERIGO, marginTop: 6, lineHeight: 1.5 }}>
                  Escrita repetida, vai entrar uma vez só:{" "}
                  {repetidas.map(([a, b]) => `"${a}" e "${b}"`).join("; ")}.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Padrão de nomeação dos núcleos.
//
// O identificador é chave estrangeira em cinco coleções e entra no ID dos
// usuários de coordenação — por isso é curto, sem espaço e sem acento. O
// nome de exibição é o que aparece no portal e nos relatórios, e esse pode
// ser escrito por extenso.
const PREFIXO_ID = "RT_";
const PREFIXO_NOME = "Revelar Talentos - ";

// Transforma o que o coordenador digita num sufixo válido de identificador:
// tira acento, troca espaço e pontuação por underline, deixa em maiúsculas.
// "Ceará" -> "CEARA";  "CFO Ceará" -> "CFO_CEARA".
function sufixoId(texto) {
  return String(texto || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Rótulos das coleções ligadas ao núcleo, para o aviso de exclusão dizer
// em português o que será destruído em vez de nomes técnicos.
const ROTULO_VINCULO = {
  users: { um: "usuário", varios: "usuários" },
  attendance_logs: { um: "registro de presença", varios: "registros de presença" },
  dispensas: { um: "dispensa", varios: "dispensas" },
  solicitacoes: { um: "solicitação", varios: "solicitações" },
  treinos_externos: { um: "treino externo", varios: "treinos externos" },
};

function listarVinculos(contagens) {
  return Object.entries(contagens || {})
    .filter(([, q]) => q > 0)
    .map(([col, q]) => {
      const r = ROTULO_VINCULO[col] || { um: col, varios: col };
      return `${q} ${q === 1 ? r.um : r.varios}`;
    });
}

// "Skate, BMX, Tênis de Mesa" -> ["Skate", "BMX", "Tênis de Mesa"]
function textoParaLista(texto) {
  return String(texto || "")
    .split(",")
    .map((m) => m.trim())
    .filter(Boolean);
}

// Converte os locais gravados para o formato editável da tela. O ponto
// fica como lat/lng numéricos, que é o que o seletor de mapa consome.
function paraFormulario(nucleo) {
  const lista = Array.isArray(nucleo.locais) ? nucleo.locais : [];
  return lista.map((l) => ({
    id: l.id || "",
    nome: l.nome || "",
    endereco: l.endereco || "",
    lat: Number.isFinite(l.lat) ? l.lat : null,
    lng: Number.isFinite(l.lng) ? l.lng : null,
  }));
}

export default function SecaoNucleos({ isAdmin = false }) {
  const [nucleos, setNucleos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState(null); // { tipo, msg }

  // Formulário de criação (só identificador e modalidades; os locais
  // entram depois, pela edição — criar núcleo e mapear pistas são
  // momentos diferentes na prática).
  const [novoSufixo, setNovoSufixo] = useState("");   // o que vem depois de RT_
  const [novoEstado, setNovoEstado] = useState("");   // o que vem depois de "Revelar Talentos - "
  const [novasModalidades, setNovasModalidades] = useState("");
  // As categorias já na criação, uma caixa por modalidade digitada.
  //
  // Estavam só na edição, e isso obrigava a criar o núcleo, procurá-lo na
  // lista, clicar em Editar e só então cadastrar. Quem está criando o
  // núcleo tem a lista de categorias na mão naquele momento.
  const [novasCategorias, setNovasCategorias] = useState({});
  // A controladora dos dados, já na criação.
  //
  // Estava só na edição, e o núcleo nascia sem ela: o termo de
  // consentimento sai com o nome da instituição e com o canal pelo qual o
  // responsável pede acesso, correção ou exclusão, então até alguém voltar
  // para preencher o termo simplesmente não era emitido. Quem abre o
  // núcleo tem o convênio na mão naquele momento.
  const [novaInstituicao, setNovaInstituicao] = useState("");
  const [novoCanal, setNovoCanal] = useState("");
  const [criando, setCriando] = useState(false);

  // Edição inline (um núcleo por vez)
  const [editandoId, setEditandoId] = useState(null);
  const [modalidadesEdicao, setModalidadesEdicao] = useState("");
  // Quantos atletas sobem ao pódio em cada modalidade. Guardado por chave
  // normalizada, do mesmo jeito que o servidor guarda.
  const [podios, setPodios] = useState({});
  // As categorias de cada modalidade, guardadas aqui como o TEXTO que a
  // coordenação escreve, uma por linha. Vira lista só na hora de salvar.
  //
  // Guardar o texto, e não a lista pronta, é o que deixa apagar uma linha no
  // meio sem a caixa se reorganizar embaixo do cursor.
  const [categorias, setCategorias] = useState({});
  const [nomeEdicao, setNomeEdicao] = useState("");
  // A controladora dos dados, que sai impressa no termo de consentimento.
  const [instituicaoEdicao, setInstituicaoEdicao] = useState("");
  const [canalEdicao, setCanalEdicao] = useState("");
  const [locaisEdicao, setLocaisEdicao] = useState([]);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const { recarregarNucleos } = useNucleos();

  // Exclusão: um núcleo por vez, com o identificador digitado de novo.
  const [excluindoId, setExcluindoId] = useState(null);
  const [confirmacao, setConfirmacao] = useState("");
  const [apagando, setApagando] = useState(false);
  const [vinculos, setVinculos] = useState(null);   // { users: n, ... }
  const [forcar, setForcar] = useState(false);

  async function recarregar() {
    setCarregando(true);
    try {
      setNucleos(await carregarNucleos());
    } catch (e) {
      setMensagem({ tipo: "erro", msg: "Erro ao carregar núcleos: " + (e.message || "") });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    recarregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCriar() {
    const id = PREFIXO_ID + sufixoId(novoSufixo);
    const nome = PREFIXO_NOME + novoEstado.trim();
    const modalidades = textoParaLista(novasModalidades);
    if (!sufixoId(novoSufixo)) {
      setMensagem({ tipo: "erro", msg: "Complete o identificador com o estado (ex: CEARA) ou instituição e estado (ex: CFO_CEARA)." });
      return;
    }
    if (!novoEstado.trim()) {
      setMensagem({ tipo: "erro", msg: "Informe o estado para compor o nome do núcleo." });
      return;
    }
    if (modalidades.length === 0) {
      setMensagem({ tipo: "erro", msg: "Informe ao menos uma modalidade." });
      return;
    }
    setCriando(true);
    setMensagem(null);
    try {
      const r = await salvarNucleo({
        acao: "criar", nucleoId: id, modalidades, nome,
        categoriasPorModalidade: listaDasCategorias(novasCategorias),
        instituicao: novaInstituicao.trim(),
        canalDeDireitos: novoCanal.trim(),
      });
      setMensagem({ tipo: "ok", msg: r.mensagem || `Núcleo ${nome} criado. Cadastre os locais de treino em "Editar".` });
      setNovoSufixo("");
      setNovoEstado("");
      setNovasModalidades("");
      setNovasCategorias({});
      setNovaInstituicao("");
      setNovoCanal("");
      await recarregar();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao criar núcleo." });
    } finally {
      setCriando(false);
    }
  }

  function iniciarEdicao(nucleo) {
    setEditandoId(nucleo.id);
    setModalidadesEdicao((nucleo.modalidades || []).join(", "));
    setPodios(nucleo.podioPorModalidade || {});
    setCategorias(textoDasCategorias(nucleo.categoriasPorModalidade));
    setNomeEdicao(nucleo.nome || nucleo.id);
    setInstituicaoEdicao(nucleo.instituicao || "");
    setCanalEdicao(nucleo.canalDeDireitos || "");
    setLocaisEdicao(paraFormulario(nucleo));
    setMensagem(null);
  }

  function cancelarEdicao() {
    setEditandoId(null);
    setModalidadesEdicao("");
    setNomeEdicao("");
    setInstituicaoEdicao("");
    setCanalEdicao("");
    setLocaisEdicao([]);
  }

  function alterarLocal(indice, campo, valor) {
    setLocaisEdicao((atual) =>
      atual.map((l, i) => {
        if (i !== indice) return l;
        // "ponto" chega do mapa como { lat, lng } e vira dois campos.
        if (campo === "ponto") return { ...l, lat: valor.lat, lng: valor.lng };
        return { ...l, [campo]: valor };
      })
    );
  }

  function adicionarLocal() {
    setLocaisEdicao((atual) => [
      ...atual,
      { id: "", nome: "", endereco: "", lat: null, lng: null },
    ]);
  }

  function removerLocal(indice) {
    setLocaisEdicao((atual) => atual.filter((_, i) => i !== indice));
  }

  async function salvarEdicao(nucleo) {
    const modalidades = textoParaLista(modalidadesEdicao);
    if (modalidades.length === 0) {
      setMensagem({ tipo: "erro", msg: "Informe ao menos uma modalidade." });
      return;
    }
    if (!nomeEdicao.trim()) {
      setMensagem({ tipo: "erro", msg: "Informe o nome do núcleo." });
      return;
    }

    // Converte e valida cada local antes de mandar. A validação de
    // verdade é do servidor; aqui é só para dar mensagem melhor,
    // apontando qual linha está errada.
    const locais = [];
    for (let i = 0; i < locaisEdicao.length; i++) {
      const l = locaisEdicao[i];
      const temPonto = Number.isFinite(l.lat) && Number.isFinite(l.lng);
      const vazio = !l.nome.trim() && !temPonto && !l.endereco.trim();
      if (vazio) continue; // linha em branco: ignora em vez de reclamar

      if (!l.nome.trim()) {
        setMensagem({ tipo: "erro", msg: `Local ${i + 1}: informe um nome (ex: "Pista do IFCE").` });
        return;
      }
      if (!temPonto) {
        setMensagem({
          tipo: "erro",
          msg: `Local ${i + 1} ("${l.nome.trim()}"): marque o ponto no mapa.`,
        });
        return;
      }
      locais.push({
        id: l.id || undefined,
        nome: l.nome.trim(),
        endereco: l.endereco.trim(),
        lat: l.lat,
        lng: l.lng,
      });
    }

    setSalvandoEdicao(true);
    setMensagem(null);
    try {
      const r = await salvarNucleo({
        acao: "editar", nucleoId: nucleo.id, modalidades, locais, nome: nomeEdicao.trim(),
        podioPorModalidade: podios,
        categoriasPorModalidade: listaDasCategorias(categorias),
        instituicao: instituicaoEdicao.trim(),
        canalDeDireitos: canalEdicao.trim(),
      });
      setMensagem({ tipo: "ok", msg: r.mensagem || `Núcleo ${nomeEdicao.trim()} atualizado.` });
      cancelarEdicao();
      await recarregar();
      // Avisa o resto do painel, que exibe o nome em cabeçalhos e relatórios.
      recarregarNucleos();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao atualizar núcleo." });
    } finally {
      setSalvandoEdicao(false);
    }
  }

  async function abrirExclusao(nucleo) {
    setExcluindoId(nucleo.id);
    setConfirmacao("");
    setForcar(false);
    setVinculos(null);
    setMensagem(null);
    try {
      const r = await contarVinculosNucleo(nucleo.id);
      setVinculos(r.contagens);
    } catch (e) {
      setMensagem({ tipo: "erro", msg: "Não consegui conferir o que está ligado a este núcleo: " + (e.message || "") });
    }
  }

  async function confirmarExclusao(nucleo) {
    setApagando(true);
    setMensagem(null);
    try {
      const r = await excluirNucleo(nucleo.id, confirmacao.trim(), forcar);
      setMensagem({ tipo: "ok", msg: r.mensagem || "Núcleo excluído." });
      setExcluindoId(null);
      setConfirmacao("");
      setForcar(false);
      setVinculos(null);
      await recarregar();
      recarregarNucleos();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao excluir núcleo." });
    } finally {
      setApagando(false);
    }
  }

  async function alternarAtivo(nucleo) {
    setMensagem(null);
    try {
      // Sem enviar `locais`: a function preserva os que já estão lá.
      const r = await salvarNucleo({
        acao: "editar",
        nucleoId: nucleo.id,
        modalidades: nucleo.modalidades || [],
        podioPorModalidade: nucleo.podioPorModalidade || {},
        ativo: !(nucleo.ativo !== false),
      });
      setMensagem({ tipo: "ok", msg: r.mensagem || "Núcleo atualizado." });
      await recarregar();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao atualizar núcleo." });
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Criar núcleo é só do administrador.
          O servidor já recusa, e o cartão sai da tela pelo mesmo motivo
          que o botão de excluir já saía: oferecer o que vai ser negado
          faz a pessoa preencher um formulário inteiro para receber um
          erro no fim. Quem não cria continua editando os núcleos que
          existem, logo abaixo. */}
      {isAdmin && (
      <Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: TEXTO, marginBottom: 4 }}>
          Cadastrar novo núcleo
        </div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginBottom: 16, lineHeight: 1.6 }}>
          Complete o estado, ou instituição e estado, nos dois campos abaixo. Os
          prefixos são fixos. O <b>identificador</b> é o código interno: fica gravado em
          todo registro de presença e entra no ID dos usuários da coordenação, por isso
          não muda depois. O <b>nome</b> é o que aparece no portal e nos relatórios, e
          esse você pode ajustar quando quiser em “Editar”.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 260, flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC }}>
                Identificador (código interno)
              </label>
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <span style={{
                  border: "1px solid #D1D5DB", borderRight: "none",
                  borderRadius: "7px 0 0 7px", padding: "9px 10px", fontSize: 14,
                  fontWeight: 800, color: AZUL, background: "#F1F5F9",
                }}>
                  {PREFIXO_ID}
                </span>
                <input
                  value={novoSufixo}
                  onChange={(e) => setNovoSufixo(e.target.value)}
                  placeholder="CEARA  ou  CFO_CEARA"
                  style={{
                    flex: 1, border: "1px solid #D1D5DB", borderRadius: "0 7px 7px 0",
                    padding: "9px 12px", fontSize: 14, color: TEXTO,
                    fontFamily: "inherit", boxSizing: "border-box", minWidth: 0,
                  }}
                />
              </div>
              <div style={{ fontSize: 12, color: novoSufixo ? VERDE_LOGO : CINZA_ESC }}>
                {novoSufixo
                  ? `Ficará: ${PREFIXO_ID}${sufixoId(novoSufixo)}`
                  : "Sem espaço e sem acento. O sistema ajusta o que você digitar."}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 260, flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC }}>
                Nome do núcleo (aparece nos relatórios)
              </label>
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <span style={{
                  border: "1px solid #D1D5DB", borderRight: "none",
                  borderRadius: "7px 0 0 7px", padding: "9px 10px", fontSize: 14,
                  fontWeight: 700, color: AZUL, background: "#F1F5F9", whiteSpace: "nowrap",
                }}>
                  {PREFIXO_NOME}
                </span>
                <input
                  value={novoEstado}
                  onChange={(e) => setNovoEstado(e.target.value)}
                  placeholder="Ceará"
                  style={{
                    flex: 1, border: "1px solid #D1D5DB", borderRadius: "0 7px 7px 0",
                    padding: "9px 12px", fontSize: 14, color: TEXTO,
                    fontFamily: "inherit", boxSizing: "border-box", minWidth: 0,
                  }}
                />
              </div>
              <div style={{ fontSize: 12, color: novoEstado ? VERDE_LOGO : CINZA_ESC }}>
                {novoEstado ? `Ficará: ${PREFIXO_NOME}${novoEstado.trim()}` : "Ex: Ceará, Bahia."}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Input
              label="Modalidades (separadas por vírgula)"
              value={novasModalidades}
              onChange={setNovasModalidades}
              placeholder="Ex: Skate, BMX, Tênis de Mesa"
              style={{ minWidth: 320, flex: 1 }}
            />
          </div>

          {/* Quem responde pelos dados, no sentido da LGPD. Os dois campos
              saem impressos no termo de consentimento que o responsável
              pelo atleta assina, e sem eles o termo não é emitido. */}
          <div style={{
            background: "#F8FAFC", border: "1px solid #E5E7EB",
            borderRadius: 9, padding: "12px 14px",
            display: "flex", flexDirection: "column", gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO, marginBottom: 2 }}>
                Quem responde pelos dados dos atletas
              </div>
              <div style={{ fontSize: 12, color: CINZA_ESC, lineHeight: 1.55 }}>
                Os dois campos saem impressos no termo de consentimento que o
                responsável assina: a instituição que decide a finalidade do
                tratamento, e o canal pelo qual o responsável pede acesso, correção
                ou exclusão dos dados. Enquanto ficarem em branco, o termo deste
                núcleo não é emitido. É de cada núcleo, por isso não vem do código.
              </div>
            </div>
            <Input
              label="Instituição responsável pelo programa"
              value={novaInstituicao}
              onChange={setNovaInstituicao}
              placeholder="Ex: Instituto Federal de Educação, Ciência e Tecnologia do Ceará"
            />
            <Input
              label="Canal para o responsável exercer seus direitos"
              value={novoCanal}
              onChange={setNovoCanal}
              placeholder="Ex: revelartalentos@ifce.edu.br"
            />
          </div>

          {/* As categorias de cada modalidade, já aqui. A caixa aparece
              assim que a modalidade é digitada acima. */}
          <CaixasDeCategoria
            modalidades={textoParaLista(novasModalidades)}
            valores={novasCategorias}
            onMudar={(k, v) => setNovasCategorias((c) => ({ ...c, [k]: v }))}
          />

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn onClick={handleCriar} disabled={criando}>
              {criando ? "Criando..." : "Criar núcleo"}
            </Btn>
          </div>
        </div>
      </Card>
      )}

      {mensagem && <StatusBox tipo={mensagem.tipo} msg={mensagem.msg} />}

      <Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: TEXTO, marginBottom: 12 }}>
          Núcleos cadastrados
        </div>

        {carregando && <div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando...</div>}
        {!carregando && nucleos.length === 0 && (
          <div style={{ fontSize: 13, color: CINZA_ESC }}>Nenhum núcleo cadastrado ainda.</div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {nucleos.map((n) => {
            const ativo = n.ativo !== false;
            const emEdicao = editandoId === n.id;
            const locais = Array.isArray(n.locais) ? n.locais : [];
            const itensVinculo = listarVinculos(vinculos);
            const temVinculo = itensVinculo.length > 0;
            return (
              <div
                key={n.id}
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 10,
                  padding: "14px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <span style={{ fontSize: 15, fontWeight: 800, color: AZUL }}>{n.nome || n.id}</span>
                    {n.nome && n.nome !== n.id && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: CINZA_ESC }}>({n.id})</span>
                    )}
                    <span
                      style={{
                        marginLeft: 10,
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: 20,
                        background: ativo ? "#DCFCE7" : "#FEE2E2",
                        color: ativo ? VERDE_LOGO : PERIGO,
                      }}
                    >
                      {ativo ? "ATIVO" : "INATIVO"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {!emEdicao && (
                      <Btn variant="secondary" onClick={() => iniciarEdicao(n)} style={{ padding: "6px 14px", fontSize: 12 }}>
                        Editar
                      </Btn>
                    )}
                    <Btn
                      variant={ativo ? "danger" : "primary"}
                      onClick={() => alternarAtivo(n)}
                      style={{ padding: "6px 14px", fontSize: 12 }}
                    >
                      {ativo ? "Inativar" : "Reativar"}
                    </Btn>
                    {isAdmin && excluindoId !== n.id && (
                      <button
                        onClick={() => abrirExclusao(n)}
                        style={{ background: "none", border: "none", color: PERIGO, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "6px 4px" }}
                      >
                        Excluir
                      </button>
                    )}
                  </div>
                </div>

                {excluindoId === n.id && (
                  <div style={{ border: `1.5px solid ${PERIGO}`, background: "#FEF2F2", borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: PERIGO, marginBottom: 6 }}>
                      Excluir o núcleo {n.nome || n.id}?
                    </div>
                    <div style={{ fontSize: 12.5, color: TEXTO, lineHeight: 1.6, marginBottom: 10 }}>
                      A exclusão é definitiva e não tem desfazer. Se este núcleo já operou e
                      você só quer tirá-lo de circulação, prefira <b>Inativar</b>: ele some
                      das telas e o histórico continua íntegro.
                    </div>

                    {vinculos === null ? (
                      <div style={{ fontSize: 12.5, color: CINZA_ESC, marginBottom: 10 }}>
                        Conferindo o que está ligado a este núcleo…
                      </div>
                    ) : itensVinculo.length === 0 ? (
                      <div style={{ fontSize: 12.5, color: TEXTO, marginBottom: 10 }}>
                        Nenhum dado ligado a este núcleo. A exclusão não afeta mais nada.
                      </div>
                    ) : (
                      <div style={{ background: "#fff", border: `1px solid ${PERIGO}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 800, color: PERIGO, marginBottom: 6 }}>
                          Isto será apagado junto, em definitivo:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: TEXTO, lineHeight: 1.7 }}>
                          {itensVinculo.map((t) => <li key={t}>{t}</li>)}
                        </ul>
                        <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 8, lineHeight: 1.6 }}>
                          As contas de acesso dessas pessoas também são removidas, e a
                          frequência delas some dos relatórios de todos os meses. Não há
                          lixeira nem restauração, só fica o registro na auditoria.
                        </div>
                        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, fontSize: 12.5, fontWeight: 700, color: PERIGO, cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={forcar}
                            onChange={(e) => setForcar(e.target.checked)}
                            style={{ marginTop: 2 }}
                          />
                          <span>Entendi e quero apagar o núcleo com todos esses dados.</span>
                        </label>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                      <Input
                        label={`Digite ${n.id} para confirmar`}
                        value={confirmacao}
                        onChange={setConfirmacao}
                        placeholder={n.id}
                        style={{ minWidth: 220 }}
                      />
                      <Btn
                        variant="danger"
                        onClick={() => confirmarExclusao(n)}
                        disabled={apagando || confirmacao.trim() !== n.id || (temVinculo && !forcar)}
                        style={{ padding: "9px 18px" }}
                      >
                        {apagando ? "Excluindo..." : "Excluir definitivamente"}
                      </Btn>
                      <Btn variant="secondary" onClick={() => { setExcluindoId(null); setConfirmacao(""); }} style={{ padding: "9px 18px" }}>
                        Cancelar
                      </Btn>
                    </div>
                  </div>
                )}

                {!emEdicao && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: CINZA_ESC }}>
                    <div>Modalidades: {(n.modalidades || []).join(", ") || "—"}</div>
                    {/* Quais modalidades ainda não têm categoria escrita.
                        Enquanto faltar, o cadastro do atleta daquela
                        modalidade segue com o campo livre, que é de onde
                        vêm as grafias repetidas. */}
                    {(() => {
                      const mapa = n.categoriasPorModalidade || {};
                      const faltando = (n.modalidades || []).filter((m) => !(mapa[chaveDaModalidade(m)] || []).length);
                      const total = Object.values(mapa).reduce((soma, l) => soma + (l || []).length, 0);
                      if (!(n.modalidades || []).length) return null;
                      return faltando.length === 0
                        ? <div>Categorias: {total} cadastrada(s)</div>
                        : (
                          <div style={{ color: ALERTA }}>
                            Sem categorias cadastradas em {faltando.join(", ")}, então ali a categoria
                            do atleta continua sendo digitada à mão.
                          </div>
                        );
                    })()}
                    {locais.length === 0 ? (
                      <div style={{ color: ALERTA }}>
                        Nenhum local de treino cadastrado, então os registros deste núcleo não são conferidos por distância.
                      </div>
                    ) : (
                      <div>
                        <div style={{ marginBottom: 2 }}>
                          {locais.length} local{locais.length !== 1 ? "is" : ""} de treino (raio de {RAIO_FIXO} m cada):
                        </div>
                        {locais.map((l, i) => (
                          <div key={l.id || i} style={{ paddingLeft: 10 }}>
                            • <b style={{ color: TEXTO }}>{l.nome}</b>
                            {l.endereco ? ` — ${l.endereco}` : ""}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {emEdicao && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div>
                      <Input
                        label="Nome do núcleo"
                        value={nomeEdicao}
                        onChange={setNomeEdicao}
                        placeholder="Ex: Centro de Formação Olímpica do Ceará"
                      />
                      <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 4, lineHeight: 1.5 }}>
                        É o nome que aparece nas telas e nos relatórios. O identificador
                        interno (<b>{n.id}</b>) não muda: ele liga este núcleo aos cadastros,
                        registros de presença e dispensas já existentes.
                      </div>
                    </div>

                    <Input
                      label="Modalidades (separadas por vírgula)"
                      value={modalidadesEdicao}
                      onChange={setModalidadesEdicao}
                    />

                    {/* A controladora dos dados, no sentido da LGPD. Sai
                        impressa no termo que o responsável assina, e era
                        constante fixa no gerador do PDF: o termo de
                        qualquer núcleo nomeava a instituição do Ceará.
                        Sem estes dois campos o termo não é emitido. */}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO, marginBottom: 2 }}>
                        Quem responde pelos dados
                      </div>
                      <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 9, lineHeight: 1.55 }}>
                        Os dois saem impressos no termo de consentimento que o responsável
                        assina. Enquanto estiverem em branco, o termo deste núcleo não é
                        emitido, para não sair com a instituição errada.
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <Input
                          label="Instituição responsável pelo programa"
                          value={instituicaoEdicao}
                          onChange={setInstituicaoEdicao}
                          placeholder="Ex: IFCE, Instituto Federal de Educação, Ciência e Tecnologia do Ceará"
                        />
                        <Input
                          label="Canal para o responsável exercer seus direitos"
                          value={canalEdicao}
                          onChange={setCanalEdicao}
                          placeholder="Ex: revelartalentos@ifce.edu.br"
                        />
                      </div>
                    </div>

                    {/* Quantos sobem ao pódio muda de esporte para esporte:
                        no BMX premia-se até o quinto, no skate e no tênis de
                        mesa até o terceiro. Sem esse número, a contagem de
                        medalhas do relatório sai errada para todos. */}
                    {textoParaLista(modalidadesEdicao).length > 0 && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO, marginBottom: 2 }}>
                          Quantos atletas sobem ao pódio
                        </div>
                        <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 9, lineHeight: 1.55 }}>
                          Muda de esporte para esporte, insira o número para cada modalidade.
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                          {textoParaLista(modalidadesEdicao).map((m) => {
                            const chave = chaveDaModalidade(m);
                            return (
                              <div key={m} style={{
                                display: "flex", alignItems: "center", gap: 8,
                                background: "#F8FAFC", border: "1px solid #E5E7EB",
                                borderRadius: 9, padding: "7px 11px",
                              }}>
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{m}</span>
                                <input type="number" min={1} max={20}
                                  value={podios[chave] === undefined ? 3 : podios[chave]}
                                  onChange={(e) => setPodios((p) => ({ ...p, [chave]: e.target.value }))}
                                  style={{ width: 52, border: "1px solid #D1D5DB", borderRadius: 10, padding: "6px 8px", fontSize: 13, textAlign: "center", fontFamily: "inherit" }} />
                                <span style={{ fontSize: 11.5, color: CINZA_ESC }}>atletas</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* As categorias de cada modalidade.
                        Antes a lista nascia do que cada um digitava no
                        cadastro do atleta, e "Girls 12 anos" convivia com
                        "Girls - 12 anos" como se fossem duas. Escrita aqui
                        uma vez, ela vira a opção no cadastro do atleta e no
                        lançamento de resultado. */}
                    <CaixasDeCategoria
                      modalidades={textoParaLista(modalidadesEdicao)}
                      valores={categorias}
                      onMudar={(k, v) => setCategorias((c) => ({ ...c, [k]: v }))}
                    />

                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO, marginBottom: 2 }}>
                        Locais oficiais de treino
                      </div>
                      <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 10, lineHeight: 1.5 }}>
                        Busque o local pelo nome ou endereço, ou clique no mapa para marcar.
                        Um registro é considerado regular se cair a até{" "}
                        <b>{RAIO_FIXO} m</b> de qualquer um destes locais. Para instalações
                        grandes, cadastre mais de um ponto em vez de um só, porque vários
                        pontos acompanham o formato real do lugar. Sem nenhum local, este núcleo não
                        é conferido por distância.
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {locaisEdicao.map((l, i) => (
                          <div key={i} style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 12, background: "#F8FAFC" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: CINZA_ESC }}>Local {i + 1}</span>
                              <button
                                onClick={() => removerLocal(i)}
                                style={{ background: "none", border: "none", color: PERIGO, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                              >
                                Remover
                              </button>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <Input
                                  label="Nome do local"
                                  value={l.nome}
                                  onChange={(v) => alterarLocal(i, "nome", v)}
                                  placeholder="Ex: Pista de BMX do IFCE"
                                  style={{ minWidth: 220, flex: 1 }}
                                />
                              </div>
                              <Input
                                label="Endereço (opcional)"
                                value={l.endereco}
                                onChange={(v) => alterarLocal(i, "endereco", v)}
                                placeholder="Ex: Av. Treze de Maio, 2081, Benfica, Fortaleza"
                              />
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC, marginBottom: 5 }}>
                                  Ponto no mapa
                                </div>
                                <SeletorLocal
                                  valor={Number.isFinite(l.lat) ? { lat: l.lat, lng: l.lng } : null}
                                  onChange={(p) => alterarLocal(i, "ponto", p)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <Btn variant="secondary" onClick={adicionarLocal} style={{ marginTop: 12, padding: "8px 16px", fontSize: 13 }}>
                        + Adicionar local
                      </Btn>
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                      <Btn onClick={() => salvarEdicao(n)} disabled={salvandoEdicao} style={{ padding: "9px 18px" }}>
                        {salvandoEdicao ? "Salvando..." : "Salvar"}
                      </Btn>
                      <Btn variant="secondary" onClick={cancelarEdicao} style={{ padding: "9px 18px" }}>
                        Cancelar
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
